import axios, { type AxiosRequestConfig } from 'axios'
import { getSessionId, Utils } from '@pbt/pbt-ui-components'
import {
  Environment,
  Region,
} from '@pbt/pbt-ui-components/src/constants/environment'
import { getDomainContext } from '@pbt/pbt-ui-components/src/utils'

import type { ProcessedEvent } from './sseCore'
import { tokenHolder } from './utils/token'

export interface MessageAttachment {
  data: string // base64 encoded data
  mimeType: string
}

export interface AgentChatMessage {
  agent?: string
  attachments?: MessageAttachment[]
  content: string
  finalReportWithCitations?: boolean
  id: string
  invocationId?: string
  isComplete?: boolean
  isLoading?: boolean
  modelVersion?: string
  processedEvents?: ProcessedEvent[]
  sender: 'user' | 'agent'
  timestamp: string
  type?: 'text' | 'error' | 'system'
}

export interface AgentChatSession {
  appName: string
  events: AgentChatMessage[]
  id: string
  lastUpdateTime: number
  state: Record<string, any>
  userId: string
}

export const getAgentBaseUrl = (): string => {
  if (Utils.isLocalEnvironment()) {
    return ''
  }

  const domainContext = getDomainContext()

  const agentUrlMap: Record<Region, Partial<Record<Environment, string>>> = {
    [Region.US]: {
      [Environment.PROD]: 'https://api.rhapsody.vet',
      [Environment.MASTER]: 'https://api.master.rhapsody.vet',
      [Environment.STAGE]: 'https://api.stage.rhapsody.vet',
    },
    [Region.EU]: {
      [Environment.PROD]: 'https://api.eu.rhapsody.vet',
      [Environment.MASTER]: 'https://api.eu.master.rhapsody.vet',
      [Environment.STAGE]: 'https://api.eu.stage.rhapsody.vet',
    },
  }

  const envApiMap = agentUrlMap[domainContext.region]
  const defaultApiUrl = envApiMap?.[Environment.STAGE]
  return envApiMap?.[domainContext.env] || defaultApiUrl || ''
}

export const agentRequest = async (
  url: string,
  config: AxiosRequestConfig = {},
  businessId?: string,
) => {
  const token = await tokenHolder.getToken()
  const effectiveBusinessId =
    businessId || localStorage.getItem('businessId') || ''

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Business-Id': effectiveBusinessId,
    'X-PBT-SessionId': getSessionId(),
    ...config.headers,
    ...(Utils.isLocalEnvironment()
      ? {
          'X-PBT-DevEnv': window.pbt.getDevEnv(),
          'X-PBT-DevRegion': window.pbt.getDevRegion(),
        }
      : {}),
  }

  try {
    const baseUrl = getAgentBaseUrl()
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

    const response = await axios({
      ...config,
      url: fullUrl,
      headers,
    })
    return response.data
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.error(
      '❌ Agent API request failed:',
      error.response?.status,
      error.response?.data,
    )
    throw error
  }
}

export const startAgentChatSession = (
  userId: string = 'user',
  businessId?: string,
) =>
  agentRequest(
    `/ai-agents/apps/agents/users/${userId}/sessions`,
    {
      method: 'POST',
      data: {
        id: crypto.randomUUID(),
        appName: 'agents',
        userId,
        state: {},
        events: [],
        lastUpdateTime: Date.now() / 1000,
      },
    },
    businessId,
  )

export const getUserSessions = (userId: string, businessId?: string) =>
  agentRequest(
    `/ai-agents/apps/agents/users/${userId}/sessions`,
    {
      method: 'GET',
    },
    businessId,
  )

export const getSession = (
  userId: string,
  sessionId: string,
  businessId?: string,
) =>
  agentRequest(
    `/ai-agents/apps/agents/users/${userId}/sessions/${sessionId}`,
    {
      method: 'GET',
    },
    businessId,
  )

export const deleteSession = (
  userId: string,
  sessionId: string,
  businessId?: string,
) =>
  agentRequest(
    `/ai-agents/apps/agents/users/${userId}/sessions/${sessionId}`,
    {
      method: 'DELETE',
    },
    businessId,
  )
